import moment from 'moment'
import { ISchedulePattern, WeekPattern } from '../../../../../../../../app/entities/SchedulePattern'

/**
 * PATTERN WEEK CALCULATION UTILITY - SOURCE OF TRUTH
 * 
 * CRITICAL: This function exists identically in both frontend and backend.
 * If you update this file, you MUST update the backend version at:
 * app/utils/scheduling/pattern-week-calculator.ts
 * 
 * PURPOSE: Determines which week pattern to use for any given date
 * ANCHOR: August 17th, 2025 (Sunday) - chosen for Sunday-Saturday week alignment
 * 
 * CONTEXT: We migrated from Monday-based WeekCalendars to Sunday-based SchedulePatterns
 * for better stakeholder visual alignment with calendar grid display.
 */

// PERMANENT ANCHOR: August 17th, 2025 (Sunday) - never changes
const PATTERN_ANCHOR_DATE = new Date('2025-08-17')

/**
 * Calculate which week pattern index to use for a given date
 * 
 * @param date - The date to calculate week pattern for
 * @param patternLength - Number of week patterns (e.g., 2 for alternating schedule)
 * @returns Week pattern index (0-based)
 * 
 * Example:
 * - August 17th, 2025 (anchor) → Week 0
 * - August 24th, 2025 → Week 1 
 * - August 31st, 2025 → Week 0 (cycles back)
 */
export const getPatternWeekIndex = (date: Date, patternLength: number): number => {
  const weeksSinceAnchor = Math.floor(
    (date.getTime() - PATTERN_ANCHOR_DATE.getTime()) / (7 * 24 * 60 * 60 * 1000)
  )
  return weeksSinceAnchor % patternLength
}

/**
 * Determines if a technician is working on a given date based on schedule pattern
 * 
 * @param schedulePattern - The schedule pattern with weekPatterns array
 * @param date - The date to check
 * @returns true if technician should be working, false otherwise
 */
export const isWorkingDay = (schedulePattern: Pick<ISchedulePattern, 'weekPatterns'>, date: Date): boolean => {
  if (!schedulePattern?.weekPatterns || schedulePattern.weekPatterns.length === 0) {
    return false
  }

  const weekIndex = getPatternWeekIndex(date, schedulePattern.weekPatterns.length)
  const weekPattern = schedulePattern.weekPatterns.find((wp: WeekPattern) => wp.week === weekIndex)
  
  // Get day name in lowercase (sunday, monday, etc.) - match backend exactly
  const dayName = date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase()
  
  return weekPattern?.days.includes(dayName) || false
}

export const getDayNameFromDate = (date: Date): string => {
  return moment(date).format('dddd').toLowerCase()
}

export const simulatePatternStatus = (
  date: Date,
  draftPattern: WeekPattern[],
  patternStartDate: Date,
): 'working' | 'off' => {
  const working = isWorkingDay({ weekPatterns: draftPattern }, date)
  return working ? 'working' : 'off'
}

// Calendar date range calculation for data fetching
export const getCalendarDateRange = (calendarDate: Date) => {
  const firstOfMonth = moment(calendarDate).startOf('month')
  const lastOfMonth = moment(calendarDate).endOf('month')

  // Find the Sunday that starts the first week containing the 1st
  const calendarStartDate = firstOfMonth.clone().startOf('week') // Sunday before/of 1st

  // Find the Saturday that ends the last week containing the last day
  const calendarEndDate = lastOfMonth.clone().endOf('week') // Saturday after/of last day

  return {
    startDate: calendarStartDate.format('YYYY-MM-DD'),
    endDate: calendarEndDate.format('YYYY-MM-DD'),
    calendarStartDate,
    calendarEndDate,
  }
}